import React, { useEffect, useState } from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import Grid from "@mui/material/Grid";
import Skeleton from "@mui/material/Skeleton";
import {
  Button,
  TextField,
  Typography,
  InputAdornment,
  Pagination,
  Modal,
  Autocomplete,
  IconButton,
} from "@mui/material";
import { Add, Close, Search } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import ClienteCard from "./detalhesCliente";
import { getClients } from "../../api/clientApi";
import { ClienteData } from "../../types/clientData";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getEmpresasData } from "../../api/dashboardApi";

const defaultTheme = createTheme({
  typography: {
    fontFamily: "Poppins, sans-serif",
  },
});

const Clientes: React.FC = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState<ClienteData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchValue, setSearchValue] = useState<string>("");
  const token = localStorage.getItem("token");
  const [openAddVehicleModal, setOpenAddVehicleModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<any>(null);
  const [companies, setCompanies] = useState<any[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(
    null
  );
  const [page, setPage] = useState<number>(1);
  const itemsPerPage = 20;

  const handleEditButtonClick = (clientId: number) => {
    navigate(`/editarCliente/${clientId}`);
  };

  const handleAddCarButtonClick = (clientId: number) => {
    navigate(`/veiculos/${clientId}`);
  };

  const handleNewClient = () => {
    setLoading(true);
    const userData = localStorage.getItem("userData");

    if (userData) {
      const parsedUserData = JSON.parse(userData);
      if (parsedUserData.admin) {
        setOpenAddVehicleModal(true);
      } else {
        navigate("/novoCliente");
      }
    } else {
      navigate("/novoCliente");
    }

    setLoading(false);
  };

  const handleCloseAddVehicleModal = () => {
    setOpenAddVehicleModal(false);
  };

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        if (!token) {
          throw new Error("Token de autenticação não encontrado");
        }
        const companiesData = await getEmpresasData(token);
        setCompanies(companiesData);
      } catch (error) {
        toast.error("Erro ao buscar empresas.");
        console.error("Erro ao buscar empresas:", error);
      }
    };

    fetchCompanies();
  }, [token]);

  const handleViewVehicleButtonClick = (
    clientId: number,
    veiculos: { [key: number]: string } | null
  ) => {
    if (veiculos && Object.keys(veiculos).length > 0) {
      navigate(`/visualizarVeiculoVinculado/${clientId}`);
    } else {
      navigate(`/veiculos/${clientId}`);
    }
  };

  const handleSelectCompany = () => {
    if (selectedCompanyId) {
      navigate(`/novoCliente/${selectedCompanyId}`);
      setOpenAddVehicleModal(false);
    } else {
      toast.error("Por favor, selecione uma empresa.");
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!token) {
          throw new Error("Token de autenticação não encontrado");
        }
        const data = await getClients(token);
        setClients(data.reverse());
      } catch (error) {
        toast.error("Erro ao buscar clientes!");
        console.error("Erro ao buscar clientes:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token]);

  const filteredClients = clients.filter(
    (client) =>
      client.documento_identificador_cliente
        .toLowerCase()
        .includes(searchValue.toLowerCase()) ||
      client.nome_cliente.toLowerCase().includes(searchValue.toLowerCase())
  );

  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedClients = filteredClients.slice(startIndex, endIndex);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
  };

  return (
    <ThemeProvider theme={defaultTheme}>
      <Box sx={{ display: "flex" }} p={2}>
        <Box sx={{ flexGrow: 1, overflow: "auto" }}>
          <Toolbar />
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: "center",
              width: { xs: "100%", sm: "auto" },
              margin: { xs: "10px 0", sm: 0 },
            }}
          >
            {loading ? (
              <Skeleton
                variant="rectangular"
                width={400}
                height={50}
                sx={{
                  borderRadius: 2,
                  mr: 5,
                }}
              />
            ) : (
              <TextField
                placeholder="Pesquisar"
                sx={{
                  width: { xs: "100%", sm: 600 },
                  margin: { xs: "0 0 10px 0", sm: 0 },
                  "& .MuiInputLabel-root.Mui-focused": { color: "#303030" },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 3,
                    backgroundColor: "white",
                  },
                  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                    {
                      borderColor: "#303030",
                    },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
              />
            )}
            {loading ? (
              <Skeleton
                variant="rectangular"
                width={200}
                height={50}
                sx={{ borderRadius: 2, mt: { xs: 5, sm: 0, md: 0, lg: 0 } }}
              />
            ) : (
              <Button
                variant="contained"
                sx={{
                  height: 50,
                  minWidth: { xs: "100%", sm: 200 },
                  backgroundColor: "#303030",
                  borderRadius: 2,
                  ml: { xs: 0, sm: 3 },
                  display: "flex",
                  alignItems: "center",
                  ":hover": { bgcolor: "#5c5c5c" },
                }}
                onClick={handleNewClient}
              >
                <Typography
                  sx={{
                    marginLeft: 1,
                    fontSize: { xs: 14, sm: 16 },
                  }}
                >
                  Adicionar Cliente
                </Typography>
                <Add sx={{ fontSize: { xs: 24, sm: 30 }, marginLeft: 2 }} />
              </Button>
            )}
          </Box>
          <Modal
            open={openAddVehicleModal}
            disableEscapeKeyDown
            disableEnforceFocus
          >
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: 400,
                bgcolor: "background.paper",
                border: "2px solid #000",
                boxShadow: 24,
                p: 4,
              }}
            >
              <IconButton
                aria-label="close"
                onClick={handleCloseAddVehicleModal}
                sx={{
                  position: "absolute",
                  right: 8,
                  top: 8,
                  color: (theme) => theme.palette.grey[500],
                }}
              >
                <Close />
              </IconButton>
              <Typography variant="h6" component="h2" gutterBottom mb={3}>
                Selecionar Empresa
              </Typography>
              <Autocomplete
                options={companies}
                getOptionLabel={(option) => option.nome_empresa}
                value={selectedCompany}
                onChange={(event, newValue) => {
                  setSelectedCompany(newValue);
                  setSelectedCompanyId(newValue?.id);
                }}
                noOptionsText="Nenhuma empresa encontrada!"
                componentsProps={{
                  popper: {
                    style: { zIndex: 1301 },
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Empresa"
                    variant="outlined"
                    fullWidth
                  />
                )}
              />

              <Button
                variant="contained"
                onClick={handleSelectCompany}
                disabled={!selectedCompany}
                fullWidth
                sx={{
                  mt: 5,
                  backgroundColor: "#1d1d1d",
                  ":hover": { bgcolor: "#5c5c5c" },
                }}
              >
                Selecionar Empresa
              </Button>
            </Box>
          </Modal>
          <Box mt={2} display="flex" justifyContent="center">
            <Pagination
              count={Math.ceil(filteredClients.length / itemsPerPage)}
              page={page}
              onChange={handlePageChange}
            />
          </Box>
          <Grid container spacing={3} mt={0.2}>
            {loading ? (
              <>
                {[...Array(5)].map((_, index) => (
                  <Grid item xs={12} key={index}>
                    <Skeleton
                      variant="rectangular"
                      height={120}
                      sx={{ borderRadius: 5 }}
                    />
                  </Grid>
                ))}
              </>
            ) : paginatedClients.length === 0 ? (
              <Typography
                variant="h6"
                textAlign={"center"}
                mt={12}
                sx={{
                  marginLeft: { xs: 6, sm: "auto" },
                  marginRight: "auto",
                }}
              >
                Nenhum cliente encontrado.
              </Typography>
            ) : (
              paginatedClients.map((client) => (
                <ClienteCard
                  key={client.id}
                  client={client}
                  handleEditButtonClick={handleEditButtonClick}
                  handleAddCarButtonClick={handleAddCarButtonClick}
                  handleViewVehicleButtonClick={handleViewVehicleButtonClick}
                />
              ))
            )}
          </Grid>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default Clientes;
